import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Upload, X, Image, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function CreateListingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [formData, setFormData] = useState({
    brand_id: '',
    model_id: '',
    price: '',
    year: '',
    mileage: '',
    body_type: '',
    power: '',
    drive_type: '',
    battery_included: false,
    vat_deductible: false,
    equipment_ids: [],
    phone: '',
    city: '',
    images: [],
    description: ''
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (id) {
      loadListing();
    }
  }, [id]);

  const loadInitialData = async () => {
    try {
      const [brandsRes, equipmentRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/brands`),
        fetch(`${BACKEND_URL}/api/equipment`)
      ]);

      const brandsData = await brandsRes.json();
      const equipmentData = await equipmentRes.json();

      setBrands(brandsData);
      setEquipment(equipmentData);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Nepodarilo sa načítať dáta');
      setLoading(false);
    }
  };

  const loadListing = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/listings/${id}`, {
        credentials: 'include'
      });
      if (!res.ok) throw new Error('Failed to load listing');
      
      const data = await res.json();
      setFormData({
        brand_id: data.brand_id,
        model_id: data.model_id,
        price: data.price,
        year: data.year,
        mileage: data.mileage,
        body_type: data.body_type,
        power: data.power,
        drive_type: data.drive_type,
        battery_included: data.battery_included,
        vat_deductible: data.vat_deductible,
        equipment_ids: data.equipment_ids || [],
        phone: data.phone,
        city: data.city || '',
        images: data.images || [],
        description: data.description || ''
      });
      
      if (data.brand_id) {
        loadModels(data.brand_id);
      }
    } catch (error) {
      console.error('Failed to load listing:', error);
      toast.error('Nepodarilo sa načítať inzerát');
    }
  };

  const loadModels = async (brandId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/models?brand_id=${brandId}`);
      const data = await res.json();
      setModels(data);
    } catch (error) {
      console.error('Failed to load models:', error);
    }
  };

  const handleBrandChange = (brandId) => {
    setFormData({ ...formData, brand_id: brandId, model_id: '' });
    if (brandId) {
      loadModels(brandId);
    } else {
      setModels([]);
    }
  };

  const handleEquipmentToggle = (equipmentId) => {
    const current = formData.equipment_ids || [];
    if (current.includes(equipmentId)) {
      setFormData({ ...formData, equipment_ids: current.filter(id => id !== equipmentId) });
    } else {
      setFormData({ ...formData, equipment_ids: [...current, equipmentId] });
    }
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Check total images limit
    if (formData.images.length + files.length > 15) {
      toast.error('Maximálne 15 obrázkov');
      return;
    }

    setUploading(true);

    for (const file of files) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} nie je obrázok`);
        continue;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} je príliš veľký (max 5MB)`);
        continue;
      }

      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);

        const res = await fetch(`${BACKEND_URL}/api/upload-image`, {
          method: 'POST',
          credentials: 'include',
          body: formDataUpload
        });

        if (!res.ok) {
          const error = await res.json();
          throw new Error(error.detail || 'Upload failed');
        }

        const data = await res.json();
        const imageUrl = data.url.startsWith('http') ? data.url : `${BACKEND_URL}${data.url}`;
        
        setFormData(prev => ({
          ...prev,
          images: [...prev.images, imageUrl]
        }));
      } catch (error) {
        console.error('Upload error:', error);
        toast.error(`Chyba pri nahrávaní ${file.name}`);
      }
    }

    setUploading(false);
    e.target.value = ''; // Reset input
  };

  const handleRemoveImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const url = id 
        ? `${BACKEND_URL}/api/listings/${id}`
        : `${BACKEND_URL}/api/listings`;
      
      const method = id ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!res.ok) throw new Error('Failed to save listing');

      const data = await res.json();
      
      if (!id) {
        await fetch(`${BACKEND_URL}/api/listings/${data.listing_id}/verify-sms`, {
          method: 'POST',
          credentials: 'include'
        });
      }

      toast.success(id ? 'Inzerát bol aktualizovaný' : 'Inzerát bol vytvorený a overený');
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to save listing:', error);
      toast.error('Nepodarilo sa uložiť inzerát');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 py-12">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-primary mb-8" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="page-heading">
            {id ? 'Upraviť inzerát' : 'Vytvoriť nový inzerát'}
          </h1>

          <Card className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Label className="text-sm font-medium text-slate-700">Výrobca *</Label>
                  <Select value={formData.brand_id} onValueChange={handleBrandChange} required>
                    <SelectTrigger data-testid="brand-select" className="touch-manipulation">
                      <SelectValue placeholder="Vyberte značku" />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={4}>
                      {brands.length > 0 ? brands.map(brand => (
                        <SelectItem key={brand.brand_id} value={brand.brand_id}>{brand.name}</SelectItem>
                      )) : <div className="px-2 py-1.5 text-sm text-slate-500">Načítavam...</div>}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700">Model *</Label>
                  <Select value={formData.model_id} onValueChange={(val) => setFormData({...formData, model_id: val})} disabled={!formData.brand_id} required>
                    <SelectTrigger data-testid="model-select" className="touch-manipulation">
                      <SelectValue placeholder="Vyberte model" />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={4}>
                      {models.length > 0 ? models.map(model => (
                        <SelectItem key={model.model_id} value={model.model_id}>{model.name}</SelectItem>
                      )) : <div className="px-2 py-1.5 text-sm text-slate-500">{formData.brand_id ? 'Načítavam...' : 'Najprv vyberte značku'}</div>}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700">Cena (€) *</Label>
                  <Input
                    data-testid="price-input"
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({...formData, price: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700">Rok výroby *</Label>
                  <Input
                    data-testid="year-input"
                    type="number"
                    min="2010"
                    max="2026"
                    value={formData.year}
                    onChange={(e) => setFormData({...formData, year: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700">Počet km *</Label>
                  <Input
                    data-testid="mileage-input"
                    type="number"
                    value={formData.mileage}
                    onChange={(e) => setFormData({...formData, mileage: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700">Typ karosérie *</Label>
                  <Select value={formData.body_type} onValueChange={(val) => setFormData({...formData, body_type: val})} required>
                    <SelectTrigger data-testid="body-type-select" className="touch-manipulation">
                      <SelectValue placeholder="Vyberte typ" />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={4}>
                      <SelectItem value="Sedan">Sedan</SelectItem>
                      <SelectItem value="SUV">SUV</SelectItem>
                      <SelectItem value="Hatchback">Hatchback</SelectItem>
                      <SelectItem value="Coupe">Coupe</SelectItem>
                      <SelectItem value="Wagon">Wagon</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700">Výkon (kW) *</Label>
                  <Input
                    data-testid="power-input"
                    type="number"
                    value={formData.power}
                    onChange={(e) => setFormData({...formData, power: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700">Pohon *</Label>
                  <Select value={formData.drive_type} onValueChange={(val) => setFormData({...formData, drive_type: val})} required>
                    <SelectTrigger data-testid="drive-type-select" className="touch-manipulation">
                      <SelectValue placeholder="Vyberte pohon" />
                    </SelectTrigger>
                    <SelectContent position="popper" sideOffset={4}>
                      <SelectItem value="Predný">Predný</SelectItem>
                      <SelectItem value="Zadný">Zadný</SelectItem>
                      <SelectItem value="4x4">4x4</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700">Telefón *</Label>
                  <Input
                    data-testid="phone-input"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <Label className="text-sm font-medium text-slate-700">Mesto *</Label>
                  <Input
                    data-testid="city-input"
                    type="text"
                    placeholder="napr. Bratislava"
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    required
                  />
                </div>
              </div>

              {/* Image Gallery */}
              <div>
                <Label className="text-sm font-medium text-slate-700 mb-3 block">Fotografie (max 15)</Label>
                
                {/* Image Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-4">
                  {formData.images.map((img, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden border border-slate-200 group">
                      <img 
                        src={img} 
                        alt={`Foto ${index + 1}`} 
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-image-${index}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                      {index === 0 && (
                        <span className="absolute bottom-1 left-1 bg-green-600 text-white text-xs px-2 py-0.5 rounded">
                          Hlavná
                        </span>
                      )}
                    </div>
                  ))}
                  
                  {/* Upload Button */}
                  {formData.images.length < 15 && (
                    <label className="aspect-square rounded-lg border-2 border-dashed border-slate-300 hover:border-green-500 hover:bg-green-50 transition-colors cursor-pointer flex flex-col items-center justify-center">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploading}
                        data-testid="image-upload-input"
                      />
                      {uploading ? (
                        <Loader2 className="h-8 w-8 text-green-600 animate-spin" />
                      ) : (
                        <>
                          <Upload className="h-8 w-8 text-slate-400 mb-2" />
                          <span className="text-xs text-slate-500 text-center px-2">Nahrať fotky</span>
                        </>
                      )}
                    </label>
                  )}
                </div>
                
                <p className="text-xs text-slate-500">
                  Prvá fotka bude hlavná. Max 5MB na fotku. Podporované: JPG, PNG, WebP
                </p>
              </div>

              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    data-testid="battery-checkbox"
                    id="battery"
                    checked={formData.battery_included}
                    onCheckedChange={(checked) => setFormData({...formData, battery_included: checked})}
                  />
                  <Label htmlFor="battery" className="text-sm cursor-pointer">S batériou</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    data-testid="vat-checkbox"
                    id="vat"
                    checked={formData.vat_deductible}
                    onCheckedChange={(checked) => setFormData({...formData, vat_deductible: checked})}
                  />
                  <Label htmlFor="vat" className="text-sm cursor-pointer">Možnosť odpočtu DPH</Label>
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700 mb-2 block">Výbava</Label>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {equipment.map(eq => (
                    <div key={eq.equipment_id} className="flex items-center space-x-2">
                      <Checkbox
                        data-testid={`equipment-checkbox-${eq.equipment_id}`}
                        id={eq.equipment_id}
                        checked={formData.equipment_ids.includes(eq.equipment_id)}
                        onCheckedChange={() => handleEquipmentToggle(eq.equipment_id)}
                      />
                      <Label htmlFor={eq.equipment_id} className="text-sm cursor-pointer">{eq.name}</Label>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label className="text-sm font-medium text-slate-700">Popis</Label>
                <Textarea
                  data-testid="description-textarea"
                  rows={5}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Popíšte vozidlo..."
                />
              </div>

              <div className="flex gap-3">
                <Button
                  data-testid="cancel-button"
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  disabled={submitting}
                >
                  Zrušiť
                </Button>
                <Button
                  data-testid="submit-button"
                  type="submit"
                  className="flex-1 bg-primary text-white hover:bg-slate-800 font-semibold"
                  disabled={submitting || uploading}
                >
                  {submitting ? 'Ukladám...' : (id ? 'Aktualizovať' : 'Vytvoriť inzerát')}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default CreateListingPage;
